/**
 * @file Transaction related fragments.
 */
import gql from 'graphql-tag';

import { printPartnerOrderServiceDetails } from './print-partner';

export const TransactionItemDetails = gql`
  fragment TransactionItemDetails on TransactionItemTypes {
    __typename
    ... on TransactionSubscriptionAlbumItem {
      album {
        id
        name
        root_album_folder {
          id
          name
        }
      }
    }
    ... on ITransactionPhotoItem {
      photo_item_type
      album {
        id
        name
        root_album_folder {
          id
          name
        }
      }
      photo {
        id
      }
      is_prepaid
    }
    ... on ITransactionSubscriptionItem {
      subscription_item_type
      identity {
        id
      }
      identity_name
    }
    ... on ITransactionItem {
      id
      item_type
      price {
        value
        currency
      }
      total_price {
        value
        currency
      }
      tax_price {
        value
        currency
      }
      tax_total_price {
        value
        currency
      }
      vendor_local_tax_price {
        value
        currency
      }
      vendor_state_tax_price {
        value
        currency
      }
      item_shipping_price {
        value
        currency
      }
      print_partner_cost_total_price {
        value
        currency
      }
      waldo_fee {
        value
        currency
      }
      waldo_fee_price {
        value
        currency
      }
      waldo_app_fee_price {
        value
        currency
      }
      stripe_fee_price {
        value
        currency
      }
      transaction_refunds(slice: { limit: 999 }) {
        records {
          id
          amount
          notes
          account {
            id
          }
        }
      }
      created_at
      transaction {
        id
        uuid
        total
        vendor_can_reprint
        stripe_connect_account_id
        payment_processor_transaction_id
        bayphoto_lookup_id
        roes_order_id
        order_number
        email_address
        account {
          id
          name
          email_address
          phone_number
        }
        account_address {
          id
          name
          address_line_1
          address_line_2
          city
          state
          post_code
          country {
            id
            name
          }
        }
        transaction_refunds(slice: { limit: 999 }) {
          records {
            id
            amount
            notes
            account {
              id
            }
          }
        }
        print_partner {
          id
          name
          contact_phone_number
          contact_email_address
          roes_info {
            lab_id
          }
        }
      }
      print_product_configurations {
        total
        records {
          id
          quantity
          front_print_file_url
          print_product_configuration {
            id
            name
          }
        }
      }
      prepaid_transaction_item {
        id
        total_price {
          value
          currency
        }
      }
      contributors {
        total
        records {
          id
          attributed_price {
            value
          }
          account {
            id
            name
            email_address
          }
        }
      }
    }

    ... on TransactionSubscriptionAlbumFolderItem {
      album_folder {
        id
        name
      }
      album_folder_name
      album_folder_is_demo
    }
    ... on TransactionSubscriptionAlbumItem {
      album {
        id
        name
        root_album_folder {
          id
          name
        }
      }
      album_name
      album_is_demo
    }
    ... on TransactionPhotoPrintItem {
      quantity
      photo_print_size {
        id
      }
      photo_print_size_name
      print_without_watermark
      shipping_cost_percentage
      tax_percentage
      is_prepaid
    }
    ... on TransactionPhotoPhotoPricingItem {
      photo_pricing {
        id
      }
      purchased_photo_pricing {
        id
        status
        identity {
          id
        }
        quantity
      }
      photo_pricing_name
      digital_downloads_purchase
      digital_downloads_price {
        value
        currency
      }
      print_without_watermark
      shipping_cost_percentage
      tax_percentage
      print_items {
        total
        records {
          quantity
          print_package_item_type {
            id
          }
          print_package_item_type_name
          price {
            value
            currency
          }
        }
      }
      print_product_configurations {
        total
        records {
          id
          front_print_file_url
          print_product_configuration {
            id
            name
          }
        }
      }
      accounting_codes
      is_prepaid
      is_prepaid_configuration
    }
    ... on TransactionPhotoPrintPackageItem {
      quantity
      print_package {
        id
      }
      print_package_name
      purchased_print_package {
        id
        status
      }
      identity {
        id
      }
      identity_name
      print_without_watermark
      digital_downloads_purchase
      print_package_price {
        value
        currency
      }
      digital_downloads_price {
        value
        currency
      }
      album_folder_tag {
        ... on IRecord {
          id
        }
      }
      album_folder_tag_name
      album_folder_tag_price {
        value
        currency
      }
      shipping_cost_percentage
      tax_percentage
      print_items {
        total
        records {
          quantity
          print_package_item_type {
            id
          }
          print_package_item_type_name
        }
      }
      print_product_configurations {
        total
        records {
          id
          front_print_file_url
          print_product_configuration {
            id
            name
          }
        }
      }
      accounting_codes
      is_prepaid
      is_prepaid_configuration
    }

    ... on ITransactionCustomPrintItem {
      custom_print_name
      custom_print {
        id
        type
        subtype
      }
      identity {
        id
      }
      identity_name
      photos_no
      quantity
      discount_purchased
      shipping_purchased
      purchased_custom_print {
        id
        order_status
        print_file_url
        photos(slice: { limit: 1 }) {
          records {
            id
            photo {
              id
              albums(slice: { limit: 1 }) {
                records {
                  id
                  name
                }
              }
            }
          }
        }
        custom_print {
          id
          name
          type
          subtype
        }
      }
      is_prepaid
      is_prepaid_configuration
    }

    ... on TransactionCustomPrintAlbumItem {
      album {
        id
        name
        root_album_folder {
          id
          name
        }
      }
      album_name
      custom_print_item_type
      purchased_custom_print {
        id
        order_status
        print_file_url
        custom_print {
          id
          name
          type
          subtype
        }
      }
      is_prepaid
      is_prepaid_configuration
    }
    ... on TransactionDigitalBundleItem {
      purchased_digital_bundle {
        id
        photos_count
        photos(slice: { limit: 20 }) {
          total
          records {
            id
            photo {
              id
              original_filename
              album {
                id
                album_type
                album_subtype
              }
              albums(slice: { limit: 1 }) {
                records {
                  id
                  album_type
                  album_subtype
                }
              }
              thumbnail_urls {
                url
                width
                height
                size_code
              }
            }
          }
        }
      }
      item_album {
        id
        name
        root_album_folder {
          id
          name
        }
      }
      item_album_folder {
        id
        name
      }
    }
    ... on TransactionPrintOrderServiceItem {
      id
      item_type
      print_order_configuration {
        print_partner_order_services {
          total
          records {
            id
            print_partner_order_service {
              ...printPartnerOrderServiceDetails
            }
          }
        }
        total_price {
          value
          currency
        }
      }
      item_album {
        id
        name
        root_album_folder {
          id
          name
        }
      }
      item_album_folder {
        id
        name
      }
    }
  }
  ${printPartnerOrderServiceDetails}
`;
